import { useState, useEffect } from "react";
import { useLang } from "../context/LanguageContext";

// Los textos de cada foto están en translations.js (hero.slides), en el mismo orden.
const SLIDES = [
  { img: "/images/hero/talleres-comunitarios.jpg", credit: "C. Faerron" },
  { img: "/images/hero/educacion-campo.jpg",       credit: "A. Sánchez" },
  { img: "/images/hero/ciencia-ciudadana.jpg",     credit: "C. Nourissat" },
  { img: "/images/hero/diagnosticos.jpg",          credit: "Nicoya Peninsula Waterkeeper" },
];
const STAT_NUMBERS = ["25+", "5000+", "5+"];
const SLIDE_MS = 4000;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// El CSS ya tiene scroll-behavior: smooth y scroll-padding-top para el menú fijo.
const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView();

export default function HeroSection({ id }) {
  const { t } = useLang();
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  // Cada vez que cambia la foto (sola o con un puntito) el temporizador arranca de cero.
  useEffect(() => {
    if (paused || prefersReducedMotion()) return undefined;
    const timer = setTimeout(() => setSlide((s) => (s + 1) % SLIDES.length), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [slide, paused]);

  return (
    <>
      <style>{`
        .sh*{box-sizing:border-box;}
        .sh{--f:#0d2218;--m:#1c3d28;--mi:#5aaa7a;--go:#b8935a;--cr:#f2ede4;font-family:'DM Sans',sans-serif;}
        .sh::before{content:'';position:absolute;inset:0;z-index:2;background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");pointer-events:none;}
        @keyframes fu{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
        .f1{animation:fu .8s .1s both}
        .f4{animation:fu .8s .55s both}.f5{animation:fu .8s .7s both}.f6{animation:fu .8s .85s both}
        @keyframes sl{0%{transform:scaleY(0);transform-origin:top}49%{transform:scaleY(1);transform-origin:top}50%{transform:scaleY(1);transform-origin:bottom}100%{transform:scaleY(0);transform-origin:bottom}}
        .scl{animation:sl 2s ease-in-out infinite;}
        @media(prefers-reduced-motion:reduce){.f1,.f4,.f5,.f6,.scl{animation:none}}
        .sdot{width:6px;height:6px;border-radius:50%;background:rgba(242,237,228,.28);cursor:pointer;transition:all .3s;border:none;padding:0;}
        .sdot.act{background:#5aaa7a;width:20px;border-radius:3px;}
        .btn-p{display:inline-flex;align-items:center;gap:8px;background:#5aaa7a;color:#0d2218;padding:13px 28px;border-radius:4px;font-size:.875rem;font-weight:500;letter-spacing:.03em;border:none;cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .25s;}
        .btn-p:focus-visible,.btn-s:focus-visible,.sdot:focus-visible{outline:2px solid #f2ede4;outline-offset:3px;}
        .btn-p:hover{background:#f2ede4;transform:translateY(-2px);box-shadow:0 12px 28px rgba(90,170,122,.28);}
        .btn-s{display:inline-flex;align-items:center;gap:8px;color:rgba(242,237,228,.72);font-size:.875rem;font-weight:300;border:none;background:none;cursor:pointer;font-family:'DM Sans',sans-serif;border-bottom:1px solid rgba(242,237,228,.25);padding-bottom:2px;transition:all .25s;}
        .btn-s:hover{color:#f2ede4;border-color:#f2ede4;}
        @media(max-width:768px){
          .sh{padding-top:80px;}
          .hi{padding:0 20px !important;align-items:flex-start;flex-direction:column;}
          .ht{font-size:clamp(1.8rem,7vw,2.4rem) !important;}
          .btn-p{width:auto !important;}
          .sr{justify-content:space-between;}
          .sr-div{margin:0 14px !important;}
          .sr-num{font-size:2rem !important;}
          .hline{display:none;}
          .hl{padding-right:0 !important;max-width:100% !important;}
          .rp{width:100% !important;height:auto !important;aspect-ratio:4/5;margin:40px 0 96px;}
          .rp-side{display:none;}
        }

      `}</style>

      <section id={id} className="sh" style={{position:"relative",minHeight:"100vh",background:"#0d2218",display:"flex",flexDirection:"column",overflow:"hidden"}}>

        {/* orb */}
        <div aria-hidden="true" style={{position:"absolute",right:"38%",top:"30%",width:480,height:480,borderRadius:"50%",background:"radial-gradient(circle,rgba(90,170,122,.1) 0%,transparent 70%)",pointerEvents:"none",zIndex:1}}/>
        {/* left line */}
        <div className="hline" aria-hidden="true" style={{position:"absolute",left:28,top:"50%",transform:"translateY(-50%)",width:1,height:"38%",background:"linear-gradient(to bottom,transparent,#5aaa7a,transparent)",opacity:.35,zIndex:3}}/>

        {/* BODY */}
        <div className="hi" style={{flex:1,display:"flex",alignItems:"center",padding:"0 64px",position:"relative",zIndex:5}}>

          {/* LEFT */}
          <div className="hl" style={{flex:1,maxWidth:640,paddingRight:48}}>

            <h1 className="ht f1" style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"clamp(3rem,4.8vw,4.4rem)",fontWeight:300,lineHeight:1.1,color:"#f2ede4",margin:"0 0 32px 0",letterSpacing:"-.01em"}}>
              {t.hero.titleLine1}<br/>{t.hero.titleLine2}
            </h1>

            <p className="f4" style={{fontSize:"1rem",fontWeight:300,lineHeight:1.75,color:"rgba(242,237,228,.62)",maxWidth:500,margin:"0 0 16px 0",letterSpacing:".01em"}}>
              {t.hero.intro1}
            </p>
            <p className="f4" style={{fontSize:"1rem",fontWeight:300,lineHeight:1.75,color:"rgba(242,237,228,.62)",maxWidth:500,margin:"0 0 40px 0",letterSpacing:".01em"}}>
              {t.hero.intro2}
            </p>

            <div className="f5" style={{display:"flex",alignItems:"center",gap:28,marginBottom:56,flexWrap:"wrap"}}>
              <button className="btn-p" onClick={()=>scrollToSection('servicios')}>{t.hero.ctaPrimary} <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg></button>
              <button className="btn-s" onClick={()=>scrollToSection('proyectos')}>{t.hero.ctaSecondary} <span aria-hidden="true">→</span></button>
            </div>

            <div className="sr f6" style={{display:"flex",alignItems:"flex-start",gap:0,paddingTop:32,borderTop:"1px solid rgba(242,237,228,.1)"}}>
              {STAT_NUMBERS.map((num,i)=>({num,label:t.hero.stats[i]})).map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:0}}>
                  {i>0&&<div className="sr-div" style={{width:1,background:"rgba(242,237,228,.12)",alignSelf:"stretch",margin:"0 32px"}}/>}
                  <div>
                    <div className="sr-num" style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"2.5rem",fontWeight:300,color:"#f2ede4",lineHeight:1}}>{s.num}</div>
                    <div style={{fontSize:".68rem",letterSpacing:".12em",color:"rgba(242,237,228,.62)",textTransform:"uppercase",marginTop:6,maxWidth:110}}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div
            className="rp"
            style={{width:"42%",height:"80vh",position:"relative",borderRadius:6,overflow:"hidden",flexShrink:0}}
            onMouseEnter={()=>setPaused(true)}
            onMouseLeave={()=>setPaused(false)}
            onFocus={()=>setPaused(true)}
            onBlur={()=>setPaused(false)}
          >
            {SLIDES.map((s,i)=>(
              <img
                key={s.img}
                src={s.img}
                alt={t.hero.slides[i]}
                aria-hidden={slide!==i}
                loading={i===0?"eager":"lazy"}
                fetchPriority={i===0?"high":"low"}
                decoding="async"
                style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",opacity:slide===i?1:0,transition:"opacity .6s ease"}}
              />
            ))}
            {/* overlays */}
            <div style={{position:"absolute",inset:0,background:"linear-gradient(to top,rgba(13,34,24,.75) 0%,rgba(13,34,24,.1) 60%,transparent 100%)",zIndex:1}}/>
            <div className="rp-side" style={{position:"absolute",inset:0,background:"linear-gradient(to right,#0d2218 0%,transparent 25%)",zIndex:1}}/>
            {/* eslogan */}
            <div style={{position:"absolute",top:24,left:24,zIndex:2,fontFamily:"'Cormorant Garamond',serif",fontSize:".8rem",fontStyle:"italic",color:"rgba(242,237,228,.78)",letterSpacing:".08em"}}>{t.hero.tagline}</div>
            {/* corner top right */}
            <div aria-hidden="true" style={{position:"absolute",top:20,right:20,width:28,height:28,borderTop:"2px solid #b8935a",borderRight:"2px solid #b8935a",zIndex:2,opacity:.7}}/>
            {/* corner bottom left */}
            <div aria-hidden="true" style={{position:"absolute",bottom:20,left:20,width:28,height:28,borderBottom:"2px solid #b8935a",borderLeft:"2px solid #b8935a",zIndex:2,opacity:.7}}/>
            {/* bottom bar */}
            <div style={{position:"absolute",bottom:24,left:24,right:24,zIndex:2,display:"flex",alignItems:"flex-end",justifyContent:"space-between"}}>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <span style={{fontSize:".72rem",letterSpacing:".15em",color:"rgba(242,237,228,.85)",textTransform:"uppercase"}}>{t.hero.slides[slide]}</span>
                <span style={{fontSize:".62rem",letterSpacing:".06em",color:"rgba(242,237,228,.7)"}}>{t.gallery.photoCredit}: {SLIDES[slide].credit}</span>
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                {SLIDES.map((_,i)=>(
                  <button key={i} className={`sdot${slide===i?" act":""}`} onClick={()=>setSlide(i)} aria-label={`${t.hero.goToSlide} ${i+1}: ${t.hero.slides[i]}`} aria-current={slide===i}/>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SCROLL */}
        <div aria-hidden="true" style={{position:"absolute",bottom:28,left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:8,zIndex:5}}>
          <div className="scl" style={{width:1,height:36,background:"linear-gradient(to bottom,#5aaa7a,transparent)"}}/>
          <span style={{fontSize:".6rem",letterSpacing:".2em",color:"rgba(242,237,228,.55)",textTransform:"uppercase"}}>{t.hero.explore}</span>
        </div>

      </section>
    </>
  );
}
